import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useServiceProviders } from "@/hooks/useServiceProviders";
import { ServiceProviderCard } from "@/components/ServiceProviderCard";
import { useMemo, useState } from "react";
import { Network, Gift, BookOpen } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";
import ReferralModal from "@/components/ReferralModal";

const HeroSection = () => {
  const [referralModalOpen, setReferralModalOpen] = useState(false);
  const {
    getRandomProviders,
    hasProviders,
    loading
  } = useServiceProviders();

  // Get random providers with randomized positions when component mounts
  // Use deferred rendering to not block LCP
  const providersWithPositions = useMemo(() => {
    if (!hasProviders || loading) return [];
    return getRandomProviders(5);
  }, [hasProviders, loading, getRandomProviders]);
  return <section className="py-8 md:py-12 bg-gradient-to-b from-background to-muted/50">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center">
          <div className="md:w-1/2 mb-6 md:mb-0">
            <h1 className="font-poppins font-bold text-4xl md:text-5xl lg:text-6xl mb-4 leading-tight">
              The <span className="text-anyskill-purple">Complete</span> Skill <span className="text-anyskill-blue">Marketplace</span>
            </h1>
            <blockquote className="font-inter text-lg text-foreground/80 mb-4 max-w-lg italic">
              "AnySkill exists to replace the 'part-time job' with the 'first-time entrepreneur.' We are building a world where professionals invest in their community, and students build their future on their own terms."
            </blockquote>
            <p className="font-inter text-base text-foreground/60 mb-6 md:mb-8 max-w-lg">
              —Shane Stamm'ler-Adamson (Founder)
            </p>
          </div>
          
          {/* Buttons positioned above image on small screens, below content on larger screens */}
          <div className="md:hidden w-full mb-4">
            <div className="grid grid-cols-2 gap-3">
              {/* First column: Offer a Service above Discover Services */}
              <div className="flex flex-col gap-3">
                <Button className="bg-anyskill-purple hover:bg-anyskill-purple/90 text-white px-4 py-4 font-inter font-medium text-base" asChild>
                  <Link to="/offer-service">Offer a Service</Link>
                </Button>
                <Button variant="outline" className="border-anyskill-blue text-anyskill-blue hover:bg-anyskill-blue hover:text-white px-4 py-4 font-inter font-medium text-base" asChild>
                  <Link to="/discover-services">Discover Services</Link>
                </Button>
              </div>
              
              {/* Second column: Request a Service above See Requested Services */}
              <div className="flex flex-col gap-3">
                <Button className="bg-anyskill-blue hover:bg-anyskill-blue/90 text-white px-4 py-4 font-inter font-medium text-base" asChild>
                  <Link to="/request-service">Request a Service</Link>
                </Button>
                <Button variant="outline" className="border-anyskill-purple text-anyskill-purple hover:bg-anyskill-purple hover:text-white px-4 py-4 font-inter font-medium text-base" asChild>
                  <Link to="/requests">Service Requests</Link>
                </Button>
              </div>
            </div>
            
            {/* Chat Rooms Button - Mobile */}
            <div className="mt-4 flex flex-col items-center gap-3">
              <Button asChild className="bg-gradient-to-r from-pink-500 via-fuchsia-500 to-indigo-500 hover:opacity-90 text-white px-6 py-4 font-inter font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                <Link to="/vocab-lab" className="flex items-center space-x-3">
                  <BookOpen className="h-6 w-6" />
                  <span className="text-white drop-shadow-sm">Open Vocabulary Lab</span>
                </Link>
              </Button>

              <Button asChild className="bg-gradient-to-r from-anyskill-purple via-anyskill-blue to-anyskill-orange hover:opacity-90 text-white px-6 py-4 font-inter font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                <Link to="/chat-rooms" className="flex items-center space-x-3">
                  <Network className="h-6 w-6" />
                  <span className="text-white drop-shadow-sm">
                    Join Chat Rooms! 🚀
                  </span>
                </Link>
              </Button>
              
              {/* Referral Button - Mobile */}
              <Button
                onClick={() => setReferralModalOpen(true)}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-6 py-4 font-inter font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                <Gift className="h-5 w-5 mr-2" />
                Refer for $10
              </Button>
            </div>
          </div>
          
          <div className="md:w-1/2 relative">
            <div className="rounded-xl overflow-hidden shadow-xl animate-float">
              <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&q=80&w=800&fm=webp" srcSet="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&q=80&w=400&fm=webp 400w,
                        https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&q=80&w=800&fm=webp 800w" sizes="(max-width: 768px) 400px, 800px" alt="Students collaborating" className="w-full h-auto" width="800" height="533" fetchPriority="high" loading="eager" />
            </div>
            
            {/* Dynamic Service Provider Cards - render after initial paint */}
            {hasProviders && providersWithPositions.map(provider => <ServiceProviderCard key={provider.key} id={provider.id} name={provider.name} category={provider.category} avatarInitials={provider.avatarInitials} avatarUrl={provider.avatarUrl} position={provider.position} className={provider.position.className} />)}
            
            {/* Fallback static cards when no dynamic data is available */}
            {!loading && !hasProviders && <>
                <div className="absolute -bottom-6 -left-6 bg-card p-4 rounded-lg shadow-lg">
                  <div className="flex items-center">
                    <UserAvatar userId="demo-user-1" avatarUrl="https://images.unsplash.com/photo-1531123414780-f74242c2b052?auto=format&fit=crop&q=80&w=100" fallback="ES" size="sm" showLevel={true} className="mr-3" />
                    <div>
                      <p className="font-inter font-medium text-card-foreground">Emma S.</p>
                      <p className="text-sm text-muted-foreground">Graphic Design</p>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-card p-4 rounded-lg shadow-lg">
                  <div className="flex items-center">
                    <UserAvatar userId="demo-user-2" avatarUrl="https://images.unsplash.com/photo-1568602471122-7832951cc4c5?auto=format&fit=crop&q=80&w=100" fallback="AM" size="sm" showLevel={true} className="mr-3" />
                    <div>
                      <p className="font-inter font-medium text-card-foreground">Alex M.</p>
                      <p className="text-sm text-muted-foreground">Web Development</p>
                    </div>
                  </div>
                </div>
              </>}
          </div>
        </div>
        
        {/* Buttons below the main content on larger screens only */}
        <div className="hidden md:block mt-3">
          <div className="grid grid-cols-2 gap-3">
            {/* First column: Offer a Service above Discover Services */}
            <div className="flex flex-col gap-3">
              <Button className="bg-anyskill-purple hover:bg-anyskill-purple/90 text-white px-4 py-4 font-inter font-medium text-base" asChild>
                <Link to="/offer-service">Offer a Service</Link>
              </Button>
              <Button variant="outline" className="border-anyskill-blue text-anyskill-blue hover:bg-anyskill-blue hover:text-white px-4 py-4 font-inter font-medium text-base" asChild>
                <Link to="/discover-services">Discover Services</Link>
              </Button>
            </div>
            
            {/* Second column: Request a Service above See Requested Services */}
            <div className="flex flex-col gap-3">
              <Button className="bg-anyskill-blue hover:bg-anyskill-blue/90 text-white px-4 py-4 font-inter font-medium text-base" asChild>
                <Link to="/request-service">Request a Service</Link>
              </Button>
              <Button variant="outline" className="border-anyskill-purple text-anyskill-purple hover:bg-anyskill-purple hover:text-white px-4 py-4 font-inter font-medium text-base" asChild>
                <Link to="/requests">Service Requests</Link>
              </Button>
            </div>
          </div>
          
          {/* Chat Rooms Button - Desktop */}
          <div className="mt-6 flex justify-center gap-4">
            <Button asChild className="bg-gradient-to-r from-pink-500 via-fuchsia-500 to-indigo-500 hover:opacity-90 text-white px-8 py-5 font-inter font-bold text-xl rounded-2xl shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300">
              <Link to="/vocab-lab" className="flex items-center space-x-4">
                <BookOpen className="h-7 w-7" />
                <span className="text-white drop-shadow-lg">Open Vocabulary Lab</span>
              </Link>
            </Button>

            <Button asChild className="bg-gradient-to-r from-anyskill-purple via-anyskill-blue to-anyskill-orange hover:opacity-90 text-white px-8 py-5 font-inter font-bold text-xl rounded-2xl shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300">
              <Link to="/chat-rooms" className="flex items-center space-x-4">
                <Network className="h-7 w-7" />
                <span className="text-white drop-shadow-lg">
                  Join Chat Rooms! 🚀✨
                </span>
              </Link>
            </Button>
            
            {/* Referral Button - Desktop */}
            <Button
              onClick={() => setReferralModalOpen(true)}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-8 py-5 font-inter font-bold text-xl rounded-2xl shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300"
            >
              <Gift className="h-7 w-7 mr-3" />
              Refer for $10
            </Button>
          </div>
          
          {/* Referral Modal */}
          <ReferralModal open={referralModalOpen} onOpenChange={setReferralModalOpen} />
        </div>
      </div>
    </section>;
};
export default HeroSection;